import React, { useEffect, useState } from 'react';
import Drawer from '@material-ui/core/Drawer';
import { useCartContext } from '../../context/CartContext';
import { useUserContext } from '../../context/UserContext';
import Button from '@material-ui/core/Button';
import { Typography, Grid, Divider } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import CartLine from '../../components/Cart/CartLine';
import { cartActionTypes, cartItemActionTypes, AuthRoutes, KeyTypes } from '../../app/enums';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { SuccessDialog } from '../Dialogs/SuccessDialog';
import { ConfirmOrderDialog } from '../Dialogs/ConfirmOrderDialog';
import { ConfirmDeleteDialog } from '../Dialogs/ConfirmDeleteDialog'
import { Carts, Searches } from '../../api/api';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { cartDrawerWidth } from '../../app/common';
import { CartItem } from '../../models/cart.interface';

const useStyles = makeStyles((theme: Theme) =>
  createStyles ({
    cartDrawerPaper: {
      width: cartDrawerWidth,
      alignItems: 'center',
    },
    cartDrawerContainer: {
      width: cartDrawerWidth-20,
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: theme.spacing(0, 0, 0),
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
    },
    pageTitle: {
      color: theme.palette.primary.dark,
      fontWeight: 'bold',
    },
    gridSpacer: {
      minHeight: '12px',
    },
    checkout: {
      margin: theme.spacing(3, 0, 3),
      flexGrow: 1,
      background: theme.palette.primary.main,
    },
  }),
);

export interface ISubmitDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CartDrawer: React.FunctionComponent<RouteComponentProps> = (props: RouteComponentProps) => {
  const classes = useStyles();
  const { state, dispatch } = useCartContext();
  const userContext = useUserContext();
  const user = userContext.state.self;
  const [ openConfirmOrder, setOpenConfirmOrder ] = useState(false);
  const [ openConfirmDelete, setOpenConfirmDelete ] = useState(false);
  const [ deleteId, setDeleteId ] = useState('');
  const [ deleteInfo, setDeleteInfo ] = useState<any>(); 
  const itemCountDisplay =  state.itemCount > 1 ? '(' + state.itemCount + ' items)' : '';
  const { history } = props;

  useEffect(() => {
    if(user.locations.length > 0 && user.locations[0] !== undefined){
      loadSelfCart(user.locations[0].id)
    }
  },[state.itemCount, state.cartId, user.locations ])

  const loadSelfCart = (locationId: string) => {
    Carts.getSelfCarts(locationId)
      .then((carts) => {
        dispatch({ type: cartActionTypes.LOAD_CART, cart: carts[0] })      
      })
      .then(() => {
        if(state.cartItems.length > 0){
          state.cartItems.forEach((item) => {
            Searches.getSearch(item.searchId)
              .then((search) => {
                dispatch({ 
                  type: cartActionTypes.UPDATE_CART_ITEM, 
                  item: item, 
                  action: cartItemActionTypes.UPDATE_CART_ITEM_VEHICLE,
                  search: search 
                })
              })
          })
        }
      })
      .catch((error) => {
        console.log('Cart Drawer error', error)
      }) 
  }

  const showConfirmOrderDialog = () => {
    setOpenConfirmOrder(true);
  };

  const handleCloseConfirmOrder = () => {
    setOpenConfirmOrder(false);
  };

  const handleCloseOrderSubmitted = () => {
    dispatch({        
      type: cartActionTypes.RESET_CART
    })
    history.push(AuthRoutes.orderDetails)
  }

  const showDeleteDialog = (item: CartItem) => {
    setOpenConfirmDelete(true);
    setDeleteInfo(getDeleteInfo(item))
    setDeleteId(item.cartItemId ?? '');
  };

  const closeDeleteDialog = () => {
    setOpenConfirmDelete(false)
  }

  const handleCartItemDelete = (cartItemId: string) => {
    Carts.deleteCartItem(state.cartId, cartItemId)
      .then((deletedId) => {
        if(user.locations.length > 0 && user.locations[0] !== undefined){
          loadSelfCart(user.locations[0].id)
        }
    }).catch((error) => {
      console.log('Cart Drawer error', error)
    }) 
    
    setOpenConfirmDelete(false)
  }

  const getDeleteInfo = (item: CartItem) => {
    let print = <div></div>
    let keyType = <p>Key Type: {KeyTypes[item.keyType as keyof typeof KeyTypes]} </p>

    let pricePrint = <p>Price:
        <NumberFormat 
          value={item.price + ''}
          displayType={'text'} 
          thousandSeparator={true} 
          prefix={'$'} 
          decimalScale={2}
          fixedDecimalScale={true}
        />
      </p>
      
    let vehiclePrint = (item.vehicleInfo !== undefined)
      ? <p>{item.vehicleInfo.year} {item.vehicleInfo.make} {item.vehicleInfo.model}</p>
      : ''

    if(item.vehicleInfo !== undefined){
      print = <div>
        {vehiclePrint}
        <p>{item.vehicleInfo.vin}</p>
        {keyType} 
        {pricePrint}
      </div>
    }

    return print
  } 

  return (
    <Drawer
      anchor="right"
      variant="persistent"
      open={state.isCartDrawerOpen}
      classes={{
        paper: classes.cartDrawerPaper,
      }}
    >
      <div className={classes.toolbar} />
      <div className={classes.cartDrawerContainer}> 
        <Grid container justifyContent="center">
          <Typography className={classes.pageTitle} variant="h6">
            Your Cart {itemCountDisplay}
          </Typography>
        </Grid>
        <div className={classes.gridSpacer} />
        <Divider/>
        <Grid container>
          <Grid item>
            {state.cartItems.map((item, index) => (
              <CartLine key={index} cartItem={item} id={item.searchId+''} showDeleteDialog={showDeleteDialog}/>
            ))}            
          </Grid>
        </Grid>
        <Divider/>
        <div className={classes.gridSpacer} />
        <Grid container justifyContent="space-between">
          <Typography variant="h6"  style={{ fontWeight: 600 }}>
            Total
          </Typography>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            <NumberFormat 
              value={state.totalAmount}
              displayType={'text'} 
              thousandSeparator={true} 
              prefix={'$'} 
              decimalScale={2}
              fixedDecimalScale={true}
              />
          </Typography>
        </Grid>
        <Grid container justifyContent="center">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="medium"
            fullWidth
            className={classes.checkout}
            onClick={() => showConfirmOrderDialog()}
            disabled={state.itemCount < 1}
          >
            Submit Order
          </Button>   
        </Grid>
      </div>
      <ConfirmOrderDialog 
        title = 'Order Summary'
        open = {openConfirmOrder} 
        id = ''
        onClose = {handleCloseConfirmOrder}
      />
       <SuccessDialog
        open = {state.openSuccessDialog} 
        title = 'Order Submitted'
        content = 'Thank you for submitting your order.  If you have any questions, please email orders@fleetgenix.com or call 866-888-4282.'
        onClose = {handleCloseOrderSubmitted}
      />
      <ConfirmDeleteDialog      
        title='Confirm Delete from Cart'
        open={openConfirmDelete} 
        onClose={closeDeleteDialog} 
        id={deleteId}
        info={deleteInfo}
        onDelete={() => {handleCartItemDelete(deleteId) }}
      />
    </Drawer>
  );
};

export default withRouter(CartDrawer);
